import React from "react";
import { Box, Divider } from "@mui/material";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import colors from "../../theme/colors";
import FormGrid from "./FormGrid";

/**
 * FormSection — a titled, optionally-described group of Rff* fields. Renders a
 * heading + description above a FormGrid, so a form reads as a stack of labelled
 * groups (e.g. "DSCR details" / "Rents and coverage used to qualify the
 * scenario.").
 *
 * Props:
 *   title — bold group heading
 *   description — muted one-liner under the title
 *   divider — draw a hairline rule above the section to separate it from the
 *     preceding group
 *   number — step number beside the title ("01"). Monospaced and zero-padded, so
 *     a stack of sections lines up regardless of how long each title is.
 *   columns, gap, rowGap, columnGap — forwarded to the inner FormGrid
 */
const FormSection = ({
  title,
  description,
  divider = false,
  number,
  columns = 12,
  gap = 2.5,
  rowGap,
  columnGap,
  sx,
  bodySx,
  children,
}) => (
  <Box sx={sx}>
    {divider && <Divider sx={{ mb: 4 }} />}
    {(title || description) && (
      <Flx column gap={0.5} sx={{ mb: 2 }}>
        {title && (
          <Flx sx={{ alignItems: "baseline", gap: 1.25 }}>
            {number && (
              <Txt
                component="span"
                sx={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12,
                  fontWeight: 500,
                  color: colors.slate400,
                }}
              >
                {String(number).padStart(2, "0")}
              </Txt>
            )}
            <Txt
              component="div"
              sx={{ fontSize: 16, fontWeight: 700, color: colors.slate900 }}
            >
              {title}
            </Txt>
          </Flx>
        )}
        {description && (
          <Txt component="div" variant="body2" color="text.secondary">
            {description}
          </Txt>
        )}
      </Flx>
    )}
    <FormGrid
      columns={columns}
      gap={gap}
      bodySx={bodySx}
      rowGap={rowGap}
      columnGap={columnGap}
    >
      {children}
    </FormGrid>
  </Box>
);

export default FormSection;
